import PropTypes from "prop-types";

import {
  Badge,
  ErrorToast,
  InfoIcon,
  Section,
  Toast,
  ToastType,
  ToggleButtonSkeleton,
} from "@features/ui";
import { Button, ToggleButton } from "@transfr-inc/dashboard-components/forms";

import {
  notificationsGroupId,
  useUpdateUserEmailPreferences,
  useUserEmailPreferences,
} from "../api";
import { MetadataItem } from "./metadata.item";

import { buttonTracking } from "@lib/tracking.helpers";
import { useRequestResetPasswordToken } from "@pages/forgot-password/api/use-send-reset-password-email";

import { ROLE_NAMES, ROLES } from "src/constants";

import "./metadata.section.scss";

export const MetadataSection = ({ user }) => {
  const {
    data: emailPreferences = {},
    isLoading: loadingEmailPreferences,
    isError: emailPreferencesError,
  } = useUserEmailPreferences();

  const {
    mutateAsync: updateEmailPreferences,
    isLoading: updatingEmailPreferences,
    error: updateEmailPreferencesError,
  } = useUpdateUserEmailPreferences();

  const notificationsEnabled = emailPreferences[notificationsGroupId]?.enabled;

  const {
    mutateAsync: sendResetPasswordEmail,
    isPending: isPasswordResetPending,
    isSuccess: isPasswordResetSuccess,
    error: passwordResetError,
  } = useRequestResetPasswordToken();

  const {
    claims: { act: role, preferred_username: username },
    email,
    location,
  } = user;

  const handleResetPassword = async (e) => {
    e.preventDefault();
    await sendResetPasswordEmail(email);
  };

  const handleToggleEmailReminders = async () => {
    await updateEmailPreferences({
      groupId: notificationsGroupId,
      enabled: !notificationsEnabled,
    });
  };

  return (
    <Section className="metadata-section">
      {username !== email && (
        <MetadataItem
          className="username-item"
          label="Username"
          value={username}
        />
      )}
      {email && (
        <MetadataItem className="email-item" label="Email" value={email} />
      )}
      <MetadataItem
        className="user-type-item"
        label="User Type"
        value={<Badge>{ROLE_NAMES[role]}</Badge>}
      >
        {role !== ROLES.student && (
          <div className="metadata-info">
            Your data will not be saved
            <InfoIcon
              variant="fa-solid"
              description={`Data is only saved for ${ROLE_NAMES[ROLES.student]} accounts.`}
            />
          </div>
        )}
      </MetadataItem>
      {email && (
        <MetadataItem
          className="password-item"
          label="Password"
          value={
            isPasswordResetSuccess ? (
              <Toast
                open
                type={ToastType.success}
                message="Password reset email has been sent!"
              />
            ) : (
              <Button
                className="reset-password"
                icon={!isPasswordResetPending && ["fa-regular", "lock-keyhole"]}
                onClick={handleResetPassword}
                disabled={isPasswordResetPending}
                loader={isPasswordResetPending}
                {...buttonTracking("profile", "reset-password")}
              >
                Reset Password
              </Button>
            )
          }
        >
          <div className="metadata-info">
            <ErrorToast
              open={!!passwordResetError}
              message="Something went wrong. Please try again."
            />
          </div>
        </MetadataItem>
      )}
      {email && (
        <MetadataItem
          description="Subscription settings for helpful Trek reminder emails."
          className="reminders-item"
          label="Reminder Emails"
          value={
            loadingEmailPreferences ? (
              <ToggleButtonSkeleton />
            ) : (
              <ToggleButton
                value={notificationsEnabled}
                onChange={handleToggleEmailReminders}
                aria-label="toggle trek reminder emails preference"
                disabled={loadingEmailPreferences || updatingEmailPreferences}
              />
            )
          }
        >
          <div className="metadata-info">
            <ErrorToast
              open={!!updateEmailPreferencesError || !!emailPreferencesError}
              message="Something went wrong. Please try again."
            />
          </div>
        </MetadataItem>
      )}
      <MetadataItem
        label="Location"
        description="We'll use your organization's location to show you career data near you."
        value={location?.msa?.name ?? "Not available"}
      />
    </Section>
  );
};

MetadataSection.displayName = "Metadata Section";

MetadataSection.propTypes = {
  user: PropTypes.object,
};
